import { isPlatformBrowser } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  NgZone,
  PLATFORM_ID,
  Signal,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { LucideIconData, X } from 'lucide-angular';
import { filter } from 'rxjs';

import { SearchOverlayService } from '../../../features/search/data/search-overlay.service';
import { AuthModalService } from '../../services/auth-modal.service';
import { CartDrawerService } from '../../services/cart-drawer.service';
import { EarlyAccessBlockedDialogService } from '../../services/early-access-blocked-dialog.service';
import { NewsletterPopupStateService } from '../../services/newsletter-popup-state.service';
import { PerronPointsModalService } from '../../services/perron-points-modal.service';
import { PrepurchaseModalService } from '../../services/prepurchase-modal.service';
import { StockAlertModalService } from '../../services/stock-alert-modal.service';
import { IconComponent } from '../icon/icon.component';
import { NewsletterFormComponent } from '../newsletter-form/newsletter-form.component';
import { NEWSLETTER_COPY } from '../newsletter-form/newsletter.copy';

/** Navegación mínima antes de ofrecer el pop-up (W6): nunca al cargar. */
export const POPUP_DELAY_MS = 150_000;

/**
 * Rutas donde el pop-up no sale (W6 + P2 del spec): pago, detalle de pedido, flujos
 * de cuenta con token, la propia confirmación del newsletter y los legales.
 * Prefijos: `/terminos` cubre `/terminos-y-condiciones`.
 */
export const POPUP_BLOCKED_PREFIXES: readonly string[] = [
  '/checkout',
  '/precompra/pago',
  '/pedido',
  '/reset-password',
  '/verificar-cuenta',
  '/newsletter',
  '/privacidad',
  '/terminos',
];

export function isPopupBlockedUrl(url: string): boolean {
  const path = url.split(/[?#]/)[0];
  return POPUP_BLOCKED_PREFIXES.some((prefix) => path.startsWith(prefix));
}

/**
 * Pop-up suave del newsletter (B9 web, W6; visual-rules §5.2). Montado una vez en
 * el layout raíz.
 *
 * - Una sola vez por navegador: marca `ps-newsletter-popup-v1` al mostrarse, al
 *   cerrarse o al suscribirse desde cualquier formulario (`NewsletterPopupStateService`).
 * - Sale a los `POPUP_DELAY_MS` desde el primer render en cliente. El `App` no se
 *   destruye al cambiar de ruta, así que el tiempo se acumula entre rutas; una
 *   recarga completa reinicia la cuenta (persistirla haría que saliera al cargar).
 * - Si toca salir en una ruta excluida o con un modal global abierto, espera a la
 *   siguiente navegación.
 * - Tarjeta anclada abajo, no modal: no roba el foco ni bloquea la página.
 * - SSR-safe: en el servidor no hace nada; el temporizador corre fuera de la zona
 *   de Angular para no retrasar la estabilidad de la app (hidratación) 150 s.
 */
@Component({
  selector: 'app-newsletter-popup',
  imports: [NewsletterFormComponent, IconComponent],
  templateUrl: './newsletter-popup.component.html',
  styleUrl: './newsletter-popup.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewsletterPopupComponent {
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private readonly state = inject(NewsletterPopupStateService);

  /** Modales y overlays globales montados en `app.html`: con cualquiera abierto, no sale. */
  private readonly modalsOpen: readonly Signal<boolean>[] = [
    inject(AuthModalService).isOpen,
    inject(CartDrawerService).isOpen,
    inject(SearchOverlayService).isOpen,
    inject(PrepurchaseModalService).isOpen,
    inject(StockAlertModalService).isOpen,
    inject(PerronPointsModalService).isOpen,
    inject(EarlyAccessBlockedDialogService).isOpen,
  ];

  protected readonly copy = NEWSLETTER_COPY;
  protected readonly closeIcon: LucideIconData = X;
  protected readonly visible = signal(false);

  /** Pasó el tiempo y está esperando una ruta/momento en que pueda salir. */
  private due = false;
  /** Ya salió (o no debe salir) en esta pestaña: no se vuelve a intentar. */
  private settled = false;
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    if (!isPlatformBrowser(inject(PLATFORM_ID))) return;

    afterNextRender(() => this.start());

    // Suscripción desde cualquier formulario (también el del propio pop-up).
    effect(() => {
      if (!this.state.subscribed()) return;
      this.settled = true;
      this.visible.set(false);
    });

    this.destroyRef.onDestroy(() => {
      if (this.timer !== null) clearTimeout(this.timer);
    });
  }

  protected close(): void {
    this.state.mark();
    this.visible.set(false);
  }

  private start(): void {
    if (this.state.isMarked()) {
      this.settled = true;
      return;
    }

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((e) => this.onNavigation(e.urlAfterRedirects));

    this.zone.runOutsideAngular(() => {
      this.timer = setTimeout(() => {
        this.timer = null;
        this.zone.run(() => {
          this.due = true;
          this.tryShow(this.router.url);
        });
      }, POPUP_DELAY_MS);
    });
  }

  private onNavigation(url: string): void {
    // Visible y el usuario entra en el pago o en un legal: se retira (la marca ya está).
    if (this.visible() && isPopupBlockedUrl(url)) {
      this.visible.set(false);
      return;
    }
    if (this.due) this.tryShow(url);
  }

  private tryShow(url: string): void {
    if (this.settled || this.state.subscribed()) return;
    if (isPopupBlockedUrl(url) || this.modalsOpen.some((open) => open())) return;

    this.settled = true;
    // Otra pestaña ya lo mostró, o el storage falla: como si ya se hubiera mostrado.
    if (this.state.isMarked() || !this.state.mark()) return;
    this.visible.set(true);
  }
}
